import { FC } from 'react'
import { Bug, AlertCircle, CalendarCheck, FileText, ClipboardList, Award } from 'lucide-react'
import CardInfoButton from './CardInfoButton'
import type { CardMeta, QhseDerivedKpis } from './types'
import { KpiCardSkeleton } from '../../../../shared/components/LoadingSkeletons'

interface Props {
  loading: boolean
  kpis: QhseDerivedKpis
  onKpiClick: (kpi: string) => void
}

const kpiMeta: Record<string, CardMeta> = {
  openDefects: {
    description: 'Active defect reports that are open or in draft state.',
    endpoint: 'GET /qhse/defects?activeOnly=true',
    logic: 'Counts defects with status "SUBMIT" or "SAVE".',
  },
  nearMissesYTD: {
    description: 'Near miss reports logged this calendar year.',
    endpoint: 'GET /qhse/near-miss-reports',
    logic: 'Filters reports where dateOfOccurrence / dateIssued starts with current year.',
  },
  openAudits: {
    description: 'Audits that have not yet been finalized.',
    endpoint: 'GET /qhse/audits',
    logic: 'Counts audits where isFinalized is false.',
  },
  openWorkPermits: {
    description: 'Work permits currently open or in draft status.',
    endpoint: 'GET /qhse/work-permits',
    logic: 'Counts permits with status "SUBMIT" or "SAVE".',
  },
  pendingInspections: {
    description: 'Inspections that have not been finalized.',
    endpoint: 'GET /qhse/inspections',
    logic: 'Counts inspections where isFinalized is false.',
  },
  expiringCerts: {
    description: 'Certificates expiring soon or overdue for renewal.',
    endpoint: 'GET /qhse/certificates (placeholder — no bulk expiry endpoint)',
    logic: 'Placeholder — returns 0 until a dedicated expiry endpoint is available.',
  },
}

const sparkPath = (color: string) => (
  <svg className='w-full h-full' viewBox='0 0 100 30' preserveAspectRatio='none'>
    <polyline fill='none' stroke={color} strokeWidth='1.5' points='0,22 15,20 30,18 45,15 60,14 75,12 90,10 100,8' />
  </svg>
)

const KpiCards: FC<Props> = ({ loading, kpis, onKpiClick }) => {
  if (loading) {
    return (
      <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6'>
        {Array.from({ length: 6 }).map((_, i) => (
          <KpiCardSkeleton key={i} />
        ))}
      </div>
    )
  }

  return (
  <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-5 md:gap-6'>
    {/* Open Defects */}
    <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('openDefects')}>
      <div className='flex items-center justify-between'>
        <div className='text-[10px] text-slate-500 mb-1'>Open Defects</div>
        <CardInfoButton meta={kpiMeta.openDefects} />
      </div>
      <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.openDefects}</div>
      <div className='text-[10px] text-red-500 flex items-center gap-0.5 mt-1'>
        <Bug className='w-3 h-3' />Requires action
      </div>
      <div className='mt-2 h-10 bg-red-50 rounded-lg relative overflow-hidden'>
        <svg className='w-full h-full' viewBox='0 0 100 30' preserveAspectRatio='none'>
          <polyline fill='none' stroke='#ef4444' strokeWidth='1.5' points='0,25 15,20 30,22 45,15 60,18 75,10 90,12 100,5' />
        </svg>
      </div>
    </div>

    {/* Near Misses YTD */}
    <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('nearMissesYTD')}>
      <div className='flex items-center justify-between'>
        <div className='text-[10px] text-slate-500 mb-1'>Near Misses (YTD)</div>
        <CardInfoButton meta={kpiMeta.nearMissesYTD} />
      </div>
      <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.nearMissesYTD}</div>
      <div className='text-[10px] text-amber-500 flex items-center gap-0.5 mt-1'>
        <AlertCircle className='w-3 h-3' />Year to date
      </div>
      <div className='mt-2 h-10 bg-amber-50 rounded-lg relative overflow-hidden'>
        {sparkPath('#f59e0b')}
      </div>
    </div>

    {/* Open Audits */}
    <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('openAudits')}>
      <div className='flex items-center justify-between'>
        <div className='text-[10px] text-slate-500 mb-1'>Open Audits</div>
        <CardInfoButton meta={kpiMeta.openAudits} />
      </div>
      <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.openAudits}</div>
      <div className='text-[10px] text-blue-500 flex items-center gap-0.5 mt-1'>
        <CalendarCheck className='w-3 h-3' />Not finalized
      </div>
      <div className='mt-2 h-10 bg-blue-50 rounded-lg relative overflow-hidden'>
        {sparkPath('#3b82f6')}
      </div>
    </div>

    {/* Open Work Permits */}
    <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('openWorkPermits')}>
      <div className='flex items-center justify-between'>
        <div className='text-[10px] text-slate-500 mb-1'>Open Work Permits</div>
        <CardInfoButton meta={kpiMeta.openWorkPermits} />
      </div>
      <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.openWorkPermits}</div>
      <div className='text-[10px] text-indigo-500 flex items-center gap-0.5 mt-1'>
        <FileText className='w-3 h-3' />Active permits
      </div>
      <div className='mt-2 h-10 bg-indigo-50 rounded-lg relative overflow-hidden'>
        {sparkPath('#6366f1')}
      </div>
    </div>

    {/* Pending Inspections */}
    <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('pendingInspections')}>
      <div className='flex items-center justify-between'>
        <div className='text-[10px] text-slate-500 mb-1'>Pending Inspections</div>
        <CardInfoButton meta={kpiMeta.pendingInspections} />
      </div>
      <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.pendingInspections}</div>
      <div className='text-[10px] text-rose-500 flex items-center gap-0.5 mt-1'>
        <ClipboardList className='w-3 h-3' />Not finalized
      </div>
      <div className='mt-2 h-10 bg-rose-50 rounded-lg relative overflow-hidden'>
        {sparkPath('#f43f5e')}
      </div>
    </div>

    {/* Certificates Expiring */}
    <div className='flex flex-col flex-1 cursor-pointer bg-white rounded-2xl p-4 border border-slate-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all' onClick={() => onKpiClick('expiringCerts')}>
      <div className='flex items-center justify-between'>
        <div className='text-[10px] text-slate-500 mb-1'>Expiring Certs</div>
        <CardInfoButton meta={kpiMeta.expiringCerts} />
      </div>
      <div className='text-2xl font-bold text-slate-900'>{loading ? '—' : kpis.expiringCerts}</div>
      <div className='text-[10px] text-teal-500 flex items-center gap-0.5 mt-1'>
        <Award className='w-3 h-3' />Pending renewal
      </div>
      <div className='mt-2 h-10 bg-teal-50 rounded-lg relative overflow-hidden'>
        {sparkPath('#14b8a6')}
      </div>
    </div>
  </div>
  )
}

export default KpiCards
